/**
 * @mention helpers for the portal comment composer (ADR-0020 P3b).
 *
 * Pure + framework-free so the tricky bits (the caret-anchored `@query`
 * detection and the submit-time mention derivation) are unit-tested apart from
 * the big page renderer. The picker is always member-scoped — these only see
 * the members list the caller passes.
 */

export interface MentionMember {
  id: string;
  name: string;
}

// A name fragment after `@`: letters/numbers/`._'-`, NO spaces (so a completed
// "@Full Name " stops triggering once you type past it), ≤40 chars. Anchored to
// the caret end + preceded by start-of-string or whitespace so an email/handle
// mid-word doesn't trigger it. A multi-word name still resolves: typing the
// first-name fragment (`@Paula`) matches "Paula Manager" via `matchMembers`.
const QUERY_RE = /(?:^|\s)@([\p{L}\p{N}._'-]{0,40})$/u;
const REPLACE_RE = /@([\p{L}\p{N}._'-]{0,40})$/u;

/** The active `@query` immediately before the caret, or null if none. */
export function activeMentionQuery(textUpToCaret: string): string | null {
  const m = QUERY_RE.exec(textUpToCaret);
  return m ? m[1] : null;
}

/** Replace the trailing `@query` in `before` with `@FullName ` (+ keep `after`). */
export function applyMention(before: string, after: string, name: string): string {
  return before.replace(REPLACE_RE, `@${name} `) + after;
}

/** Members matching the active query (case-insensitive, capped). */
export function matchMembers(query: string | null, members: MentionMember[], limit = 6): MentionMember[] {
  if (query == null) return [];
  const q = query.toLowerCase();
  return members.filter((m) => m.name.toLowerCase().includes(q)).slice(0, limit);
}

/** The mentioned member ids — a member whose `@Name` still appears in the body.
 * Re-derived at submit so edits/deletes are honoured; the server re-validates
 * membership regardless. */
export function deriveMentions(text: string, members: MentionMember[]): string[] {
  return members.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);
}
